import React, { useEffect, useRef, useMemo, useState, memo } from 'react';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { TubeCurve } from '../shape/Spline';
import { Line } from '../shape/Line';

import { useFrame } from '@react-three/fiber';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial'
import { Line2 } from 'three/examples/jsm/lines/Line2'

import MPoints2DAdd from '../marker/mPoints2DAdd';

import Param from '../store/param';

import { 
  offsetCurve,
  intersectionPointsPlane,
} from './utils';

/*
import vtkCutter from '@kitware/vtk.js/Filters/Core/Cutter'
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray'
import vtkPoints from '@kitware/vtk.js/Common/Core/Points'
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData'
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor'
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper'
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer'
import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow'
import vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor'
*/

import { MakeSplinePoints } from "../shape/Spline";
import {Points, Point} from './Point';
import { FilterListOutlined, PlayCircleFilledOutlined, RadioButtonUncheckedSharp } from '@material-ui/icons';


const LineVariableWidth = React.forwardRef(function Line({ points, color = 'black', vertexColors, lineWidth, dashed, ...rest }, ref) {
  const [line2] = React.useState(() => new Line2())
  const [lineMaterial] = React.useState(() => new LineMaterial())
  const [resolution] = React.useState(() => new THREE.Vector2(512, 512))

  const lineGeom = React.useMemo(() => {
    const geom = new LineGeometry()
    const pValues = points.map((p) => (p instanceof THREE.Vector3 ? p.toArray() : p))
    geom.setPositions(pValues.flat())

    if (vertexColors) {
      const cValues = vertexColors.map((c) => (c instanceof THREE.Color ? c.toArray() : c))
      geom.setColors(cValues.flat())
    }

    const linewidth = new THREE.Float32BufferAttribute(geom.attributes.position.count, 1)
    for (let i = 0; i < geom.attributes.position.count; i++) {
      linewidth.array[i] = i
    }
    geom.setAttribute('linewidth', linewidth)

    return geom
  }, [points, vertexColors])

  React.useLayoutEffect(() => {
    line2.computeLineDistances()
  }, [points, line2])

  React.useLayoutEffect(() => {
    if (dashed) {
      lineMaterial.defines.USE_DASH = ''
    } else {
      // Setting lineMaterial.defines.USE_DASH to undefined is apparently not sufficient.
      delete lineMaterial.defines.USE_DASH
    }
    lineMaterial.needsUpdate = true
  }, [dashed, lineMaterial])

  return (
    <primitive dispose={undefined} object={line2} ref={ref} {...rest}>
      <primitive dispose={undefined} object={lineGeom} attach="geometry" />
      <primitive
        dispose={undefined}
        object={lineMaterial}
        attach="material"
        color={color}
        vertexColors={Boolean(vertexColors)}
        resolution={resolution}
        linewidth={lineWidth}
        dashed={dashed}
        {...rest}
        onBeforeCompile={(sh) => {
          sh.vertexShader = sh.vertexShader.replace('uniform float linewidth', 'attribute float linewidth')
          sh.vertexShader = sh.vertexShader.replace('uniform float linewidth', 'attribute float linewidth')
        }}
      />
    </primitive>
  )
})


/*
function vtkCrossSectionSphere() {
  const vtkContainerRef = useRef(null)

  useEffect(() => {
    const polyData = vtkPolyData.newInstance()
    const points = vtkPoints.newInstance()
    const numberOfPoints = 32
    const radius = 1.0

    for (let i = 0; i < numberOfPoints; i++) {
      const theta = (i * (2 * Math.PI)) / numberOfPoints
      const x = radius * Math.cos(theta)
      const y = radius * Math.sin(theta)
      points.insertNextPoint(x, y, 0)
    }

    polyData.setPoints(points)

    const cutter = vtkCutter.newInstance()
    cutter.setInputData(polyData)
    cutter.setCutFunction([0, 0, 1, 0])

    const cutOutput = cutter.getOutputData()
    const cutPoints = cutter.getPoints()
    const cutLines = cutOutput.getLines()

    const dataArray = new Float32Array(cutPoints.getNumberOfPoints() * 3)

    for (let i = 0; i < cutPoints.getNumberOfPoints(); i++) {
      const point = cutPoints.getPoint(i)
      dataArray[i * 3] = point[0]
      dataArray[i * 3 + 1] = point[1]
      dataArray[i * 3 + 2] = point[2]
    }

    const curve = vtkDataArray.newInstance({
      name: 'points',
      numberOfComponents: 3,
      values: dataArray
    })

    const pointsPerLine = cutLines.getData()
    const indices = []

    for (let i = 0; i < pointsPerLine.length; i += 2) {
      const startIndex = pointsPerLine[i]
      const endIndex = pointsPerLine[i + 1]

      if (endIndex - startIndex > 1) {
        for (let j = startIndex; j < endIndex - 1; j++) {
          indices.push(2)
          indices.push(j)
          indices.push(j + 1)
        }
      }
    }

    const lines = vtkDataArray.newInstance({
      name: 'lines',
      numberOfComponents: 3,
      values: new Uint32Array(indices)
    })

    curve.setNumberOfComponents(3)
    curve.setNumberOfTuples(dataArray.length / 3)

    const output = vtkPolyData.newInstance()
    output.setPoints(cutPoints)
    output.setLines(lines)
    output.getPointData().setScalars(curve)

    const actor = vtkActor.newInstance()
    const mapper = vtkMapper.newInstance()
    const renderer = vtkRenderer.newInstance({ background: [0.2, 0.3, 0.4] })
    const renderWindow = vtkRenderWindow.newInstance({ background: [0.2, 0.3, 0.4] })
    const interactor = vtkRenderWindowInteractor.newInstance({ style: 'trackball' })
    mapper.setInputData(output)
    actor.setMapper(mapper)
    renderer.addActor(actor)
    renderWindow.addRenderer(renderer)
    interactor.setView(renderWindow.getRenderWindow())
    interactor.initialize()
    interactor.bindEvents(vtkContainerRef.current)
    interactor.start()
  }, [])

  return (
    <div ref={vtkContainerRef} />
  )
}
*/

const getProportionPoint = (px, pz, segment, length, dx, dz) => {
  const factor = segment / length;

  return [(px - dx * factor), (pz - dz * factor)];
}

const filletLine = (points, radius) => {
  const p1x = points[0].x // prev
  const p1z = points[0].z // prev
  const px = points[1].x // curr
  const pz = points[1].z // curr
  const p2x = points[2].x // next
  const p2z = points[2].z // next

  //Vector 1
  const dx1 = px - p1x;
  const dz1 = pz - p1z;

  //Vector 2
  const dx2 = px - p2x;
  const dz2 = pz - p2z;

  // Now you can get vectors from points and angle between vectors
  //var angle = (Math.atan(dx1 / dz1) - Math.atan(dx2 / dz2)) / 2
  var angle = (Math.atan2(dz1 , dx1) - Math.atan2(dz2 , dx2)) / 2
  
  // Get the length of segment between angular point and the points of intersection with the circle.
  var segment = radius / Math.abs(Math.tan(angle));
  //console.log(segment)

  // Check the segment
  const length1 = Math.sqrt(dx1**2 + dz1**2); // Length of PP1
  const length2 = Math.sqrt(dx2**2 + dz2**2); // Length of PP2
  //console.log(length1, length2)

  // if segment > PP1 or segment > PP2 then you need to decrease the radius:
  const length = Math.min(length1, length2);
  if (segment > length) {
    segment = length;
    radius = segment * Math.abs(Math.tan(angle));
  }

  // Points of intersection are calculated by the proportion between 
  // the coordinates of the vector, length of vector and the length of the segment.
  var p1Cross = getProportionPoint(px, pz, segment, length1, dx1, dz1);
  var p2Cross = getProportionPoint(px, pz, segment, length2, dx2, dz2);

  // Calculation of the coordinates of the circle 
  // center by the addition of angular vectors.
  const dx = px * 2 - p1Cross[0] - p2Cross[0];
  const dz = pz * 2 - p1Cross[1] - p2Cross[1];

  //
  const l = Math.sqrt(dx**2 + dz**2);
  const d = Math.sqrt(radius**2 + segment**2);

  const centerPoint = getProportionPoint(px, pz, d, l, dx, dz);

  // StartAngle and EndAngle of arc
  // z axis (depth) is negative, because the coordinate is opengl one
  var startAngle = Math.atan2(-(p1Cross[1]-centerPoint[1]) , (p1Cross[0]-centerPoint[0]));
  var endAngle = Math.atan2(-(p2Cross[1]-centerPoint[1]) , (p2Cross[0]-centerPoint[0]));

  //console.log(startAngle*180/Math.PI, endAngle*180/Math.PI);

  // Sweep angle
  var sweepAngle = endAngle - startAngle;

  //Some additional checks
  if (sweepAngle < 0) {
    startAngle = endAngle;
    sweepAngle = -sweepAngle;
  }
  //if (sweepAngle > Math.PI) {
  //  sweepAngle = Math.PI - sweepAngle;
  //}
  endAngle = startAngle + sweepAngle;

  var startPoint = [centerPoint[0] + radius*Math.cos(startAngle), centerPoint[1] - radius*Math.sin(startAngle)]
  var endPoint = [centerPoint[0] + radius*Math.cos(endAngle), centerPoint[1] - radius*Math.sin(endAngle)]

  return {centerPoint, startPoint, endPoint, p1Cross, p2Cross, startAngle, endAngle, radius};
}



// AK base shape
export const BaseShape = memo(({
  apDiameter,
  ischiumCircumference,
  rectusFemorisOverhang,
  posteriorWallAngle,
  adjustmentLengthPosteriorWall,
  point1X,
  point1Z,
  point4X,
  point4Z,
  point3X,
  point3Z,
  point5X,
  point5Z,
  filletSideWall,
  filletScalpa,
  filletAdductorLongus,
  filletIschialTuberosity,
  compression,
  vlines, 
  color, 
  no, 
  colorHigh, 
  callback 
}) => {
  const setParam   = Param.Get('setParam');

  var key = 0
  var temp = 0;

  const [obj, setObj]  = useState([]);
  

  useEffect(() => {
    var objects = [];
    // AP line
    const pointA = new THREE.Vector3(0, 0, apDiameter/2);
    const pointP = new THREE.Vector3(0, 0, -apDiameter/2);

    //objects.push(<Line key={key} points={[pointA, pointP]} color={color} lineWidth={5} />);
    //key += 1;

    // ML line
    var mlLength = ischiumCircumference/3;
    const pointM = new THREE.Vector3(0, 0, 0);
    const pointL = new THREE.Vector3(mlLength, 0, 0);
    //objects.push(<Line key={key} points={[pointM, pointL]} color={color} lineWidth={5} />);
    //key += 1;

    // Divide points 
    const cnt = 13;
    var point = null;
    var pointsMlPosterior = [];
    var pointsMlAnterior = [];
    const divLength = mlLength / (cnt-1);
    for (let i = 0; i < cnt; i++) {
      if (i == cnt-1) {
        point = new THREE.Vector3(mlLength, 0, -apDiameter/2);
        pointsMlPosterior.push(point);
        point = new THREE.Vector3(mlLength, 0, apDiameter/2);
        pointsMlAnterior.push(point);
      } else {
        point = new THREE.Vector3(i*divLength, 0, -apDiameter/2);
        pointsMlPosterior.push(point);
        point = new THREE.Vector3(i*divLength, 0, apDiameter/2);
        pointsMlAnterior.push(point);
      }
    }

    // Side wall curve
    var pointsSideWall = [];
    var tempPoints = []
    var refPoints = []
    
    //
    pointsSideWall.push(pointA);

    //
    point = new THREE.Vector3(mlLength/3, 0, pointsMlAnterior[2].z);
    pointsSideWall.push(point);

    //
    point = pointsMlAnterior[8].clone();
    point.z += rectusFemorisOverhang;
    pointsSideWall.push(point);

    //
    point = pointsMlAnterior[11].clone();
    pointsSideWall.push(point);

    //
    pointsSideWall.push(pointL);
    
    //
    point = pointsMlPosterior[10].clone();
    pointsSideWall.push(point);

    // angle could be a little bit different as setting value.
    const offset = Math.tan((posteriorWallAngle-90) / 180 * Math.PI);
    //temp = pointsMlPosterior[2].x * offset;
    //temp = pointsMlPosterior[2].z + temp;
    temp = pointsMlPosterior[0].x * offset;
    temp = pointsMlPosterior[0].z + temp;

    
    const endSideWallX = mlLength*2/3 + adjustmentLengthPosteriorWall;
    const endSideWallZ = temp - endSideWallX*offset;
    point = new THREE.Vector3(endSideWallX, 0, endSideWallZ);
    pointsSideWall.push(point);

    point = new THREE.Vector3(0, 0, pointsMlPosterior[0].z);
    pointsSideWall.push(point);

    pointsSideWall[1].x += point1X;
    pointsSideWall[1].z += point1Z;
    pointsSideWall[3].x += point3X;
    pointsSideWall[3].z += point3Z;
    pointsSideWall[4].x += point4X;
    pointsSideWall[4].z += point4Z;
    pointsSideWall[5].x += point5X;
    pointsSideWall[5].z += point5Z;

    var temp = JSON.parse(JSON.stringify(pointsSideWall)); // deep copy
    objects.push(<Points key={key} vpoints={temp} color={colorHigh} size={2}/>);
    key += 1;

    for (let i = 0; i < pointsSideWall.length; i++) {
      objects.push(<Text
        key={key}
        position={[pointsSideWall[i].x-5, pointsSideWall[i].y, pointsSideWall[i].z]}
        rotation={[-Math.PI / 2, 0, 0]}
        color='white' // テキストの色を設定します
        fontSize={10} // フォントサイズを設定します
        maxWidth={200} // 最大幅を設定します（オプション）
      >
        {i}
      </Text>
      );
      key += 1;
    }

    /*
    const dummyCallback = (points) => {
    }

    
    objects.push(<MPoints2DAdd 
                          vpoints={temp} 
                          colorLine={'cyan'} 
                          colorDrag={colorHigh} 
                          colorClick={'pink'}
                          size1={1}
                          size2={0.5}
                          showtype={''} 
                          callback={dummyCallback}
    />);
    */

    // spline curve
    // TODO: check detail
    //var splinePoints = MakeSplinePoints(pointsSideWall.slice(1,7), false, 100)
    var splinePoints = MakeSplinePoints(pointsSideWall.slice(1,6), false, 30)
    
    // line
    //for (let i = 0; i < pointsSideWall.length-1; i++) {
    //  objects.push(<Line key={key} points={[pointsSideWall[i],pointsSideWall[i+1]]} color={color} lineWidth={5}/>);
    //  key += 1;
    //}
    
    // TODO: check detail
    pointsSideWall.splice(1, 4, ...splinePoints);





    // https://stackoverflow.com/questions/7702773/produce-pdf-files-draw-polygons-with-rounded-corners
    // https://stackoverflow.com/questions/24771828/how-to-calculate-rounded-corners-for-a-polygon
    
    // fillet side wall
    var filletPoints = filletLine(pointsSideWall.slice(pointsSideWall.length-3), filletSideWall);
    pointsSideWall.splice(pointsSideWall.length-1, 0, new THREE.Vector3(filletPoints.endPoint[0], 0, filletPoints.endPoint[1]));
    pointsSideWall[pointsSideWall.length-3] = new THREE.Vector3(filletPoints.startPoint[0], 0, filletPoints.startPoint[1]);
    
    const divN = 10;
    var sweepAngle = filletPoints.endAngle - filletPoints.startAngle;
    var divAngle = sweepAngle / divN;
    refPoints = [];
    for (let i = 0; i < divN; i++) {
      if (i==0) continue;
      var angle = (filletPoints.startAngle+divAngle*i);
      point = new THREE.Vector3(filletPoints.centerPoint[0]+filletPoints.radius*Math.cos(angle), 0, filletPoints.centerPoint[1]-filletPoints.radius*Math.sin(angle));
      tempPoints.push(point);
    }

    pointsSideWall.splice(pointsSideWall.length-2, 0, ...tempPoints);


    
    // fillet scalpa
    console.log('fillet scalpa');
    var filletPoints = filletLine(pointsSideWall.slice(0,3), filletScalpa);
    pointsSideWall.splice(1, 0, new THREE.Vector3(filletPoints.endPoint[0], 0, filletPoints.endPoint[1]));
    pointsSideWall[2] = new THREE.Vector3(filletPoints.startPoint[0], 0, filletPoints.startPoint[1]);

    sweepAngle = filletPoints.endAngle - filletPoints.startAngle;
    divAngle = sweepAngle / divN;
    tempPoints = [];
    for (let i = 0; i < divN; i++) {
      if (i==0) continue;
      var angle = (filletPoints.startAngle+divAngle*i);
      point = new THREE.Vector3(filletPoints.centerPoint[0]+filletPoints.radius*Math.cos(angle), 0, filletPoints.centerPoint[1]-filletPoints.radius*Math.sin(angle));
      tempPoints.push(point);
    }
    tempPoints = tempPoints.reverse();

    // TODO: fix bugs to draw large fillet
    /*
    for (let i = 0; i < tempPoints.length-1; i++) {
      objects.push(<LineVariableWidth key={key} points={[tempPoints[i],tempPoints[i+1]]} color='white' lineWidth={5}/>);
      key += 1;
    }

    var temp = pointsSideWall.slice(0,3)
    for (let i = 0; i < temp.length-1; i++) {
      objects.push(<LineVariableWidth key={key} points={[temp[i],temp[i+1]]} color='deeppink' lineWidth={5}/>);
      key += 1;
    }
    */

    pointsSideWall.splice(2, 0, ...tempPoints);
    

    // fillet Adductor Longus
    console.log('Adductor Longus')
    
    var filletPoints = filletLine([pointsSideWall[pointsSideWall.length-1], pointsSideWall[0], pointsSideWall[1]], filletAdductorLongus);
    pointsSideWall.splice(0, 0, new THREE.Vector3(filletPoints.startPoint[0], 0, filletPoints.startPoint[1]));
    pointsSideWall[1] = new THREE.Vector3(filletPoints.endPoint[0], 0, filletPoints.endPoint[1]);

    sweepAngle = filletPoints.endAngle - filletPoints.startAngle;
    // TODO: bug handling, but just first aid.
    if (sweepAngle > Math.PI) {
      sweepAngle -= Math.PI;
      filletPoints.startAngle = filletPoints.endAngle;
      filletPoints.endAngle = filletPoints.startAngle;
    }
    divAngle = sweepAngle / divN;
    tempPoints = [];
    refPoints = [];
    for (let i = 0; i < divN; i++) {
      if (i==0) continue;
      var angle = (filletPoints.startAngle+divAngle*i);
      point = new THREE.Vector3(filletPoints.centerPoint[0]+filletPoints.radius*Math.cos(angle), 0, filletPoints.centerPoint[1]-filletPoints.radius*Math.sin(angle));
      tempPoints.push(point);
    }
    pointsSideWall.splice(1, 0, ...tempPoints);

    // TODO: bug handling, but just first aid.
    if (pointsSideWall[0].x > pointsSideWall[0+divN].x) {
      const temp = pointsSideWall[0];
      pointsSideWall[0] = pointsSideWall[0+divN];
      pointsSideWall[0+divN] = temp; 
    }

    // fillet Ischial Tuberosity
    console.log('Ischial Tuberosity')

    var filletPoints = filletLine([pointsSideWall[pointsSideWall.length-2], pointsSideWall[pointsSideWall.length-1], pointsSideWall[0]], filletIschialTuberosity);
    pointsSideWall.splice(pointsSideWall.length-1, 0, new THREE.Vector3(filletPoints.endPoint[0], 0, filletPoints.endPoint[1]));
    pointsSideWall[pointsSideWall.length-1] = new THREE.Vector3(filletPoints.startPoint[0], 0, filletPoints.startPoint[1]);

    // TODO: check plus minus
    sweepAngle = filletPoints.endAngle - Math.abs(filletPoints.startAngle);
    
    // TODO: bug handling, but just first aid.
    filletPoints.startAngle = filletPoints.endAngle;
    filletPoints.endAngle = filletPoints.startAngle;

    divAngle = -sweepAngle / divN;
    tempPoints = [];
    refPoints = [];
    for (let i = 0; i < divN; i++) {
      if (i==0) continue;
      var angle = (filletPoints.startAngle+divAngle*i);
      point = new THREE.Vector3(filletPoints.centerPoint[0]+filletPoints.radius*Math.cos(angle), 0, filletPoints.centerPoint[1]-filletPoints.radius*Math.sin(angle));
      tempPoints.push(point);
    }
    //tempPoints = tempPoints.reverse();

    var idx = pointsSideWall.length-2;
    pointsSideWall.splice(pointsSideWall.length-1, 0, ...tempPoints);

    
    // TODO: bug handling, but just first aid.
    if (pointsSideWall[idx].x < pointsSideWall[idx+divN].x) {
      var temp = pointsSideWall.slice(idx,idx+divN+1)
      temp = temp.reverse()
      pointsSideWall.splice(idx, temp.length, ...temp);
    }
    

    //objects.push(<Points key={key} vpoints={refPoints} color={color} size={3}/>);
    //key += 1;

    //objects.push(<Points key={key} vpoints={pointsSideWall} color={colorHigh} size={2}/>);
    //key += 1;

    /*
    // line
    for (let i = 0; i < pointsSideWall.length-1; i++) {
      objects.push(<Line key={key} points={[pointsSideWall[i],pointsSideWall[i+1]]} color={color} lineWidth={5}/>);
      key += 1;
    }
    // close curve
    objects.push(<Line key={key} points={[pointsSideWall[pointsSideWall.length-1],pointsSideWall[0]]} color={color} lineWidth={5}/>);
    key += 1;
    */

    function getLengthCurve(points) {
      // CatmullRomCurve3を作成します
      const curve = new THREE.CatmullRomCurve3(points);

      // CatmullRomCurve3をCurvePathに変換します
      const curvePath = new THREE.CurvePath();
      curvePath.curves.push(curve);

      // 曲線の長さを取得します
      const length = curvePath.getLength();

      return length;
    }


    function getCentroidFromPoints(points) {
      const centroid = new THREE.Vector3(0, 0, 0);
      let signedArea = 0;
    
      for (let i = 0; i < points.length; i++) {
        const p1 = points[i];
        const p2 = points[(i + 1) % points.length]; // 最後の点の次は最初の点に戻る
    
        const area = p1.x * p2.z - p2.x * p1.z;
        signedArea += area;
    
        centroid.x += (p1.x + p2.x) * area;
        centroid.z += (p1.z + p2.z) * area;
      }
    
      signedArea *= 0.5;
      centroid.x /= 6 * signedArea;
      centroid.z /= 6 * signedArea;
    
      return centroid;
    }

    pointsSideWall.push(pointsSideWall[0]);

    var centroid = getCentroidFromPoints(pointsSideWall);
    const measuredCurveLength = getLengthCurve(pointsSideWall);
    // Calculate ML diameter
    var intersections = intersectionPointsPlane(pointsSideWall, new THREE.Vector3(0, 0, 1))
    console.assert(intersections.length === 2, intersections.length);
    const measuredMlDiameter = intersections[0].distanceTo(intersections[1]);

    // Calculate AP diameter
    intersections = intersectionPointsPlane(pointsSideWall, new THREE.Vector3(1, 0, 0), -measuredMlDiameter/6);
    console.assert(intersections.length === 2, intersections.length);
    const measuredApDiameter = intersections[0].distanceTo(intersections[1]);

    // compression
    var compressedPointsSideWall = offsetCurve(new THREE.CatmullRomCurve3(pointsSideWall), compression/100, centroid);
    var minX = 999999;
    minX = Math.min(...compressedPointsSideWall.map(p => p.x));
    compressedPointsSideWall = compressedPointsSideWall.map(p => new THREE.Vector3(p.x-minX, p.y, p.z));
    // compressed curve length
    const compressedCurveLength = getLengthCurve(compressedPointsSideWall);
    centroid = getCentroidFromPoints(compressedPointsSideWall);


    //callback({position:pointsSideWall, key:'points'});
    callback({position:compressedPointsSideWall, key:'points'});
    setParam(s => {s.baseshape.compresedCurveLength = compressedCurveLength}); // TODO: add rewrite as callback
    setParam(s => {s.baseshape.measuredCurveLength = measuredCurveLength}); // TODO: add rewrite as callback
    setParam(s => {s.baseshape.centroid = centroid})
    //objects.push(<TubeCurve key={key} vpoints={pointsSideWall} radius={1} color={color}/>)
    objects.push(<TubeCurve key={key} vpoints={compressedPointsSideWall} radius={1} color={color}/>)
    key += 1;

    // Draw circumference length
    objects.push(<Text
      key={key}
      position={[-20, 0, -200]}
      rotation={[-Math.PI / 2, 0, 0]}
      color='white'
      fontSize={15}
      maxWidth={400} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      Circumference: {Math.round(measuredCurveLength)} mm
    </Text>
    );
    key += 1;

    // Compressed circumference
    objects.push(<Text
      key={key}
      position={[-20, 0, -180]}
      rotation={[-Math.PI / 2, 0, 0]}
      color='white'
      fontSize={15}
      maxWidth={400} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      Compressed circumference: {Math.round(compressedCurveLength)} mm
    </Text>
    );
    key += 1;

    // Compressed radius
    var compressedRadius = (compressedCurveLength - measuredCurveLength) / (2*Math.PI);

    objects.push(<Text
      key={key}
      position={[-20, 0, -160]}
      rotation={[-Math.PI / 2, 0, 0]}
      color='white'
      fontSize={15}
      maxWidth={400} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      Compressed radius: {Math.round(compressedRadius*10)/10} mm
    </Text>
    );
    key += 1;










    // Calculate ML diameter
    var intersections = intersectionPointsPlane(compressedPointsSideWall, new THREE.Vector3(0, 0, 1))
    console.assert(intersections.length === 2, intersections.length);
    const mlDiameter = intersections[0].distanceTo(intersections[1]);

    // Calculate AP diameter
    intersections = intersectionPointsPlane(compressedPointsSideWall, new THREE.Vector3(1, 0, 0), -mlDiameter/6);
    console.assert(intersections.length === 2, intersections.length);
    const apDiameterOutput = intersections[0].distanceTo(intersections[1]);
    setParam(s => {s.baseshape.apDiameterOutput = apDiameterOutput}); // TODO: add rewrite as callback

    objects.push(<Line key={key} vpoints={[new THREE.Vector3(mlDiameter/6, 0, -100), new THREE.Vector3(mlDiameter/6, 0, 100)]} color={color}/>);
    key += 1;

    // Draw ML diameter
    objects.push(<Text
      key={key}
      position={[-20, 0, -140]}
      rotation={[-Math.PI / 2, 0, 0]}
      color='white'
      fontSize={15}
      maxWidth={400} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      ML Diameter: {Math.round(mlDiameter)} ({Math.round(measuredMlDiameter)}) mm
    </Text>
    );
    key += 1;

    
    // Draw AP diameter
    objects.push(<Text
      key={key}
      position={[-20, 0, -120]}
      rotation={[-Math.PI / 2, 0, 0]}
      color='white'
      fontSize={15}
      maxWidth={400} // option
      textAlign='left' // option
      anchorX='left' // option
      anchorY='middle' // option
    >
      AP Diameter: {Math.round(apDiameterOutput)} ({Math.round(measuredApDiameter)}) mm
    </Text>
    );
    key += 1;

    setObj(objects);
  },[apDiameter,
    ischiumCircumference,
    rectusFemorisOverhang,
    posteriorWallAngle,
    adjustmentLengthPosteriorWall,
    point1X,
    point1Z,
    point3X,
    point3Z,
    point4X,
    point4Z,
    point5X,
    point5Z,
    filletSideWall,
    filletScalpa,
    filletAdductorLongus,
    filletIschialTuberosity,
    compression]);
  
  return obj.length !== 0 ? (
    <React.Fragment>{obj}</React.Fragment>
  ) : null;
});
